import Long from 'long'
import type {ContinuousSaveServer, SaveTransactionResponse} from './csaveTypes'
import type {Channel, WixInstanceProvider} from '../channelUtils/duplexer'
import {MockCSDuplexer} from './mockCSChannel'
import type {
    ServerCreateRevisionResponse,
    CreateTransactionRequest,
    CreateTransactionResponse,
    GetTransactionResponse,
    GetTransactionsResponse,
    SaveRequest,
    GetDocumentResponse
} from './serverProtocolTypes'
import type {ModifySiteAppsResponse} from '@wix/ambassador-wix-html-editor-webapp/types'

const emptyInitialStore: GetDocumentResponse = {
    actions: []
}

export const nextTransactionId = (current: string): string => {
    const prev = Long.fromString(current) ?? Long.fromNumber(0)
    const plusOne = prev.add(1)
    return plusOne.toString()
}

class DocumentServer {
    private _last: Long = Long.fromString('0')
    private _lastUpdatedOnServer: Long = Long.fromString('0')
    private _lastSaveDate: Date | undefined = undefined

    get last(): string {
        return this._last.toString()
    }

    get lastUpdatedOnServer(): string {
        return this._lastUpdatedOnServer.toString()
    }

    set last(val: string) {
        this._last = Long.fromString(val)
    }
    set lastUpdatedOnServer(val: string) {
        this._lastUpdatedOnServer = Long.fromString(val)
    }

    get lastSaveDate(): Date | undefined {
        return this._lastSaveDate
    }

    set lastSaveDate(val: Date | undefined) {
        this._lastSaveDate = val
    }

    public getLastPlusOne(): string {
        return nextTransactionId(this.last)
    }
}

export class TestServer implements ContinuousSaveServer {
    public duplexer = new MockCSDuplexer()
    protected server = new DocumentServer()
    private nextTransaction?: GetTransactionResponse
    private nextTransactions?: GetTransactionsResponse = {
        transactions: []
    }
    private instanceProvider: WixInstanceProvider | null = null
    private revision: number = 1

    setNextTransaction(value?: GetTransactionResponse): void {
        this.nextTransaction = value
    }

    setNextTransactions(value?: GetTransactionsResponse): void {
        this.nextTransactions = value
    }

    constructor(private initialStore: GetDocumentResponse = emptyInitialStore, lastTxId: string = '0') {
        this.server.last = lastTxId
    }

    async onChannelReady(): Promise<void> {}

    setLast(val: string) {
        this.server.last = val
        this.server.lastUpdatedOnServer = val
    }

    getLast(): string | undefined {
        return this.server.last
    }

    setLastUpdatedOnServer(val: string) {
        this.server.lastUpdatedOnServer = val
    }

    getLastUpdatedOnServer(): string | undefined {
        return this.server.lastUpdatedOnServer
    }

    setLastSaveDate(date: Date) {
        this.server.lastSaveDate = date
    }

    getLastSaveDate(): Date | undefined {
        return this.server.lastSaveDate
    }

    nextTxId(): string {
        return this.server.getLastPlusOne()
    }

    async createRevision(): Promise<ServerCreateRevisionResponse> {
        this.revision++
        return {actions: [], siteRevision: {revision: this.revision.toString(), version: '0'}}
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async save(payload: CreateTransactionRequest): Promise<CreateTransactionResponse> {
        const transactionId = this.server.getLastPlusOne()
        return {transactionId}
    }

    setInstanceProvider(provider: WixInstanceProvider) {
        this.instanceProvider = provider
    }

    getInstance(): string | null {
        // @ts-ignore
        return this.instanceProvider?.() ?? null
    }

    createDuplexer(): Channel {
        return this.duplexer
    }

    async deleteTransactions(): Promise<void> {}

    setInitialStore(store: GetDocumentResponse) {
        this.initialStore = store
    }

    async getStore(): Promise<GetDocumentResponse> {
        return this.initialStore
    }

    async getStoreWithCache(): Promise<GetDocumentResponse> {
        return this.initialStore
    }

    async getTransactions(): Promise<GetTransactionsResponse> {
        if (!this.nextTransactions) {
            throw new Error('No payload set')
        }
        return this.nextTransactions
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async asyncSave(payload: SaveRequest): Promise<void> {}

    async getTransaction(): Promise<GetTransactionResponse> {
        if (this.nextTransaction) {
            return this.nextTransaction
        }
        throw new Error('No payload set')
    }

    async saveSyncWithMultipleTransactions(payload: SaveRequest): Promise<SaveTransactionResponse> {
        throw new Error(`not implemented ${JSON.stringify(payload)}`)
    }

    async post(): Promise<{branches: any[]}> {
        return {branches: []}
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async modifySiteApps(actions: any[], siteRevision: string, branchId: string): Promise<ModifySiteAppsResponse> {
        const clientSpecMap = {}
        return {clientSpecMap, experimentsPerAppDefId: {}}
    }
}
